const escape = value => String(value == null ? '' : value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))
const value = input => escape(input == null || input === '' ? 'Non renseigné' : input)
const date = input => String(input || '').replace(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}:\d{2}:\d{2}))?.*$/, (_, y, m, d, time) => d + '/' + m + '/' + y + (time ? ' à ' + time : ''))
const shortSex = input => {
    const raw = input == null ? '' : String(input).trim()
    const normalized = raw.toLocaleLowerCase('fr-FR').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    if (['h', 'm', 'homme', 'masculin', 'masculine', 'male'].includes(normalized)) return 'M'
    if (['f', 'femme', 'feminin', 'feminine', 'female'].includes(normalized)) return 'F'
    return raw
}

// The server already cleans new reports. Keep the print document inert as well:
// no attributes, URLs, remote resources or executable elements can enter the frame.
function reportHtml(input) {
    const root = document.createElement('div')
    root.innerHTML = String(input || '')
    const allowed = ['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'H4', 'BLOCKQUOTE', 'SUB', 'SUP', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD']
    const dangerous = ['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH', 'IMG', 'LINK', 'META', 'FORM', 'INPUT']
    const clean = node => {
        if (node.nodeType === 3) return escape(node.nodeValue)
        if (node.nodeType !== 1 || dangerous.includes(node.tagName)) return ''
        const children = Array.from(node.childNodes).map(clean).join('')
        if (!allowed.includes(node.tagName)) return children
        const tag = node.tagName.toLowerCase()
        return tag === 'br' ? '<br>' : '<' + tag + '>' + children + '</' + tag + '>'
    }
    return Array.from(root.childNodes).map(clean).join('')
}

export default {
    name: 'ImagerieImpression',
    props: { published: Object, context: { type: Object, default: () => ({}) } },
    data() { return { printing: false, error: '' } },
    computed: {
        printable() { const p = this.published; return !!(p && p.status === 'validated' && Number(p.version) > 0 && p.content && p.author_name && p.updated_at) }
    },
    beforeDestroy() { this.clearPrint() },
    methods: {
        clearPrint() {
            if (this.printFrame) { this.printFrame.remove(); this.printFrame = null }
            this.printing = false
        },
        async printReport() {
            if (!this.printable || this.printing) return
            this.error = ''; this.printing = true
            const p = this.published, c = this.context
            const identity = value(c.patient || c.nom_patient), reference = value(c.reference || c.reference_bulletin || ('Demande ' + c.demande_id))
            const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Compte rendu - ${identity} - v${escape(p.version)}</title>
<style>
@page { size: A4; margin: 16mm 16mm 20mm; @bottom-center { content: "Page " counter(page) " / " counter(pages); font: 9pt Arial, sans-serif; color: #566078; } }
@media print { body > :not(.imagerie-report-document) { display: none !important; } }
* { box-sizing: border-box; } body { margin: 0; color: #202735; background: white; font: 11pt/1.45 Arial, sans-serif; }
h1 { color: #334565; font-size: 19pt; line-height: 1.25; margin: 4mm 0; } h2 { font-size: 14pt; } h3, h4 { font-size: 12pt; }
header { border-bottom: 1px solid #afbbce; padding-bottom: 3mm; margin-bottom: 4mm; } .clinic { font-size: 15pt; font-weight: bold; color: #334565; }
.meta { border: 1px solid #ccd3df; padding: 3mm; margin: 4mm 0; } .meta p { margin: 1mm 0; } .clinical { white-space: pre-wrap; }
p { margin: 0 0 3mm; orphans: 3; widows: 3; } h1, h2, h3, h4 { break-after: avoid; page-break-after: avoid; }
.report { overflow-wrap: anywhere; word-wrap: break-word; } .report table { border-collapse: collapse; width: 100%; } .report th, .report td { border: 1px solid #aeb7c6; padding: 2mm; }
.report tr, .signature { break-inside: avoid; page-break-inside: avoid; } .report blockquote { border-left: 2px solid #aeb7c6; padding-left: 3mm; }
.signature { margin-top: 6mm; border-top: 1px solid #ccd3df; padding-top: 3mm; } .signature p { margin: 1mm 0; }
.document-footer { margin-top: 5mm; color: #566078; font-size: 9pt; border-top: 1px solid #ccd3df; padding-top: 2mm; }
</style></head><body><article class="imagerie-report-document">
<header><div class="clinic">${value(c.nom_clinique)}</div><div>Imagerie médicale · ${reference}</div></header>
<h1>${value(p.title || 'Compte rendu')}</h1>
<div class="meta"><p><strong>Examen :</strong> ${value(c.type_examen_nom || c.nom_examen)}</p>
<p><strong>Patient :</strong> ${identity}</p><p><strong>Naissance :</strong> ${value(date(c.date_naissance))} · <strong>Âge :</strong> ${value(c.age)} · <strong>Sexe :</strong> ${value(shortSex(c.sexe))}</p>
<p><strong>Téléphone :</strong> ${value(c.telephone || c.tel_pers_a_prevenir)}</p><p><strong>Prescripteur :</strong> ${value(c.nom_prescripteur || c.prescripteur)}</p>
<p class="clinical"><strong>Renseignement clinique :</strong> ${value(c.renseignement_clinique)}</p></div>
<main class="report">${reportHtml(p.content)}</main>
<section class="signature"><p><strong>Validé par :</strong> ${value(p.author_name)} · Radiologue</p><p><strong>Validation :</strong> ${value(date(p.updated_at))} (heure serveur)</p><p><strong>Version ${escape(p.version)} — Validée</strong></p></section>
<footer class="document-footer">${identity} · ${reference} · Version ${escape(p.version)}. Ce document restitue la version validée ; les brouillons ne sont pas imprimés.</footer>
</article></body></html>`
            try {
                const frame = document.createElement('iframe')
                frame.title = 'Impression du compte rendu validé'; frame.setAttribute('aria-hidden', 'true')
                frame.setAttribute('sandbox', 'allow-same-origin allow-modals')
                frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:210mm;height:1px;border:0;'
                this.printFrame = frame; document.body.appendChild(frame)
                const doc = frame.contentDocument
                doc.open(); doc.write(html); doc.close()
                frame.contentWindow.addEventListener('afterprint', () => this.clearPrint(), { once: true })
                if (doc.fonts && doc.fonts.ready) await doc.fonts.ready
                await this.$nextTick()
                if (this.printFrame !== frame) return
                frame.contentWindow.focus(); frame.contentWindow.print()
            } catch (_) { this.clearPrint(); this.error = 'Impossible de préparer l’impression. Réessayez.' }
        }
    }
}
